import { twMerge } from "tailwind-merge";

import { useAssessmentGradingFormContext } from "../../providers/AssessmentGradingFormProvider";
import { useAssessedCountData, useAssessmentNavData } from "../../utils/assessment";
import { useAv } from "../../utils/av";
import { useQueryParamsManager } from "../../utils/routing";

import { withActiveAv } from "components/av/AvSubsystemLoader";
import { SUBMISSION_ID_QUERY_PARAM } from "components/constants";
import { BaseFooter } from "components/footer/BaseFooter";
import EndClassMenu from "components/footer/EndClassMenu";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Tooltip } from "mds/components/Tooltip";
import { RoundedButton, RoundedButtonProps } from "mds/components/buttons/RoundedButton";
import { useIsSmOrLarger } from "mds/hooks/use-responsive";
import { ChevronLeftIcon, ChevronRightIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import {
  selectCurrentClassUserIsInPersonClass,
  selectCurrentPage,
  selectCurrentSectionUserId,
  selectFullSubmissionById,
} from "store/selectors";

const LazyVideoMicControls = withActiveAv(() => import("components/av/VideoMicControls"));

export const AssessmentProgressBar = () => {
  const { isAiAssessmentLoading: isLoading } = useAssessmentGradingFormContext();
  const selectedPage = useAppSelector(selectCurrentPage);
  const { onBack, onNext, prevId, isLastPanel } = useAssessmentNavData(selectedPage);
  const sectionUserId = useAppSelector(selectCurrentSectionUserId);
  const { getQueryParam } = useQueryParamsManager();
  const { isInVirtualClass } = useAv();
  const isInInPersonClass = useAppSelector(selectCurrentClassUserIsInPersonClass);
  const isInClassSession = isInVirtualClass || isInInPersonClass;

  const submissionId = getQueryParam(SUBMISSION_ID_QUERY_PARAM);
  const submission = useAppSelector((s) => selectFullSubmissionById(s, submissionId));

  const isNextDisabled = !sectionUserId || isLastPanel || isLoading;
  const isPrevDisabled = !sectionUserId || !prevId || isLoading;

  const assessedCountData = useAssessedCountData(selectedPage);
  const { count, total } = assessedCountData;

  const isMobile = !useIsSmOrLarger();

  // Early return after all hooks
  if (!selectedPage) return null;

  const percentage = total === 0 ? 0 : Math.ceil((count / total) * 100);
  const safePercentage = Math.min(100, Math.max(0, percentage));

  const submitBtnAttrs: Omit<RoundedButtonProps, "kind"> = {
    form: "assessment-grading-form",
    type: "submit",
    name: "submitSource",
  };
  const prevBtnAttrs: Omit<RoundedButtonProps, "kind"> = submission
    ? { ...submitBtnAttrs, value: "prev" }
    : { onClick: (e) => onBack(e as React.MouseEvent<HTMLButtonElement>) };
  const nextBtnAttrs: Omit<RoundedButtonProps, "kind"> = submission
    ? { ...submitBtnAttrs, value: "next" }
    : { onClick: onNext };

  const darkMode = isInClassSession;

  return (
    <BaseFooter darkMode={darkMode}>
      <LazyVideoMicControls />
      <div
        className={twMerge(
          "flex w-full items-center justify-center gap-3 text-sm font-medium text-black-tint-40",
          darkMode && "text-white",
        )}
      >
        <RoundedButton
          disabled={isPrevDisabled}
          eventName="Assessment - Previous"
          eventProps={{ pageId: selectedPage.id, submissionId: submission?.id }}
          kind="secondary"
          {...prevBtnAttrs}
        >
          <IconText iconStart={<ChevronLeftIcon />} text={t("common.previous")} />
        </RoundedButton>

        <div
          className={twMerge(
            "flex items-center justify-end whitespace-nowrap",
            !isMobile && "w-[60px]",
          )}
        >
          {t("common.index_of_total_counter", { index: count, total })}
        </div>

        {!isMobile && (
          <>
            <div className="flex w-full max-w-[1000px] flex-grow">
              <div className="h-3 w-full rounded-full bg-green-tint-70">
                <div
                  className="duration-400 h-3 rounded-full bg-green-shade-20 transition-[width] ease-in-out"
                  style={{ width: `${safePercentage}%` }}
                />
              </div>
            </div>

            <div className="flex w-[40px] items-center justify-start">
              {t("common.percentage", { number: safePercentage })}
            </div>
          </>
        )}

        <Tooltip
          text={!sectionUserId ? t("assessment_card.cannot_assess_without_enrollment") : undefined}
        >
          <RoundedButton
            className={
              isNextDisabled
                ? "group-data-[dark]/themable:bg-black-tint-20 group-data-[dark]/themable:text-white"
                : undefined
            }
            disabled={isNextDisabled}
            eventName="Assessment - Next"
            eventProps={{ pageId: selectedPage.id, submissionId: submission?.id }}
            kind="success"
            {...nextBtnAttrs}
          >
            <IconText iconEnd={<ChevronRightIcon />} text={t("common.next")} />
          </RoundedButton>
        </Tooltip>
      </div>
      {isInClassSession && (
        <div className="pl-2">
          <EndClassMenu />
        </div>
      )}
    </BaseFooter>
  );
};
